import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, CATEGORIES, LOGO_URL } from "@/lib/pfc";
import TalentCard from "@/components/TalentCard";
import ArticleCard from "@/components/ArticleCard";
import BreakingTicker from "@/components/BreakingTicker";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import { ArrowRight, Mic2, Crown, Trophy, Briefcase, Clapperboard, Flame } from "lucide-react";

const ICONS = { Mic2, Crown, Trophy, Briefcase, Clapperboard };

// Featured videos pinned to the home page (order matters — first plays right under the hero image)
const COVER_VIDEO = {
  youtube_id: "8cNgBTGUAC8",
  title: "Yumepai — Turned Chic (Official Video)",
  subtitle: "Cover Story · Atlanta's Hyperpop-Rave Queen · Now Playing",
};
const HOT_SINGLE = {
  youtube_id: "yHUqbKHp7u0",
  title: "Backshots — Dre RealA × IsGmex",
  subtitle: "New Hot Single · Recorded at Hoodstar Studio · Mixed by Slim",
  buy_url: "https://payhip.com/b/RUPIT",
  slug_link: "/magazine/new-hot-single-dre-reala-isgmex-backshots",
};

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [profiles, setProfiles] = useState([]);

  useEffect(() => {
    api.get("/articles?limit=10").then((r) => setArticles(r.data));
    api.get("/profiles?featured=true").then((r) => setProfiles(r.data));
  }, []);

  const hero = articles.find((a) => a.featured) || articles[0];
  const rest = articles.filter((a) => a.article_id !== hero?.article_id).slice(0, 6);

  return (
    <div data-testid="home-page">
      <BreakingTicker />

      {/* HERO */}
      {hero && <ArticleCard article={hero} variant="hero" />}

      {/* COVER VIDEO — Yumepai (1st video, right under hero image) */}
      <section className="bg-ink-800 border-y border-white/5 py-14" data-testid="home-cover-video">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-10">
          <div className="mb-6">
            <div className="label text-pfc-gold mb-2 flex items-center gap-2">
              <Crown size={14} className="text-pfc-gold" /> {COVER_VIDEO.subtitle}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white uppercase leading-[0.95]">
              {COVER_VIDEO.title}
            </h2>
          </div>
          <YouTubeEmbed videoId={COVER_VIDEO.youtube_id} title="Watch — Cover Story" />
        </div>
      </section>

      {/* HOT SINGLE — Backshots (pinned below cover video) */}
      <section className="bg-ink-800 border-b border-white/5 py-14" data-testid="home-hot-single">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-10">
          <div className="mb-6">
            <div className="label text-pfc-gold mb-2 flex items-center gap-2">
              <Flame size={14} className="text-red-500" /> Hot Single · Now Playing
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white uppercase leading-[0.95]">
              {HOT_SINGLE.title}
            </h2>
            <p className="text-white/60 font-body text-sm mt-2">{HOT_SINGLE.subtitle}</p>
          </div>
          <YouTubeEmbed videoId={HOT_SINGLE.youtube_id} title="Backshots (Official Video)" />
        </div>
      </section>

      {/* CATEGORY STRIP */}
      <section className="bg-ink-800 border-y border-white/5 py-10" data-testid="category-strip">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {CATEGORIES.map((c) => {
            const Icon = ICONS[c.icon];
            return (
              <Link
                key={c.key}
                to={`/directory?category=${c.key}`}
                className="group flex flex-col items-center text-center p-6 border border-white/5 hover:border-pfc-gold/50 transition"
                data-testid={`cat-link-${c.key}`}
              >
                <Icon className="text-pfc-gold mb-3 group-hover:scale-110 transition" size={28} />
                <span className="font-sub uppercase tracking-[0.2em] text-xs text-white/80 group-hover:text-white">{c.label}</span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* FEATURED TALENT */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20" data-testid="featured-talent">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
          <div>
            <div className="label text-pfc-gold mb-3">The Cover Series · 2026</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white uppercase leading-none">Featured <span className="gold-text">Talent</span></h2>
          </div>
          <Link to="/directory" className="font-sub uppercase tracking-[0.2em] text-xs text-pfc-gold hover:text-pfc-gold-bright flex items-center gap-2" data-testid="see-all-talent">
            See full directory <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {profiles.slice(0, 4).map((p) => (
            <TalentCard key={p.profile_id} profile={p} />
          ))}
        </div>
      </section>

      {/* TAGLINE BLOCK */}
      <section className="bg-ink-800 border-y border-white/5 py-24 relative grain" data-testid="tagline-block">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-10 text-center relative">
          <img src={LOGO_URL} alt="PFC" className="h-32 w-32 mx-auto mb-8 object-contain opacity-90" />
          <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl leading-[0.95] uppercase">
            <span className="gold-text">Where Public Figures,</span><br />
            <span className="silver-text">Creators & Visionaries</span><br />
            <span className="gold-text">Get Seen.</span>
          </h2>
          <p className="text-white/60 max-w-xl mx-auto mt-8 font-body">
            A digital magazine, press network, and promotional hub built for the rising and the relentless.
          </p>
          <Link to="/submit" className="inline-block mt-10 font-sub uppercase tracking-[0.25em] text-sm px-8 py-4 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition hover:scale-105" data-testid="cta-submit-story">
            Submit Your Story →
          </Link>
        </div>
      </section>

      {/* MAGAZINE GRID */}
      <section className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20" data-testid="magazine-grid">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-3">
          <div>
            <div className="label text-pfc-gold mb-3">The Magazine</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white uppercase leading-none">Latest <span className="gold-text">Stories</span></h2>
          </div>
          <Link to="/magazine" className="font-sub uppercase tracking-[0.2em] text-xs text-pfc-gold hover:text-pfc-gold-bright flex items-center gap-2" data-testid="see-all-stories">
            Browse magazine <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((a) => <ArticleCard key={a.article_id} article={a} />)}
        </div>
      </section>
    </div>
  );
}
